import React, { Fragment, useState } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTheme } from "@/context/ThemeContext";

interface TAMDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const SEGMENTS = [
  {
    name: "Premium",
    who: "Higher income, ages 45–65, older homes",
    ticket: 1000,
    freq: 2.5,
    color: "#B8432F",
  },
  {
    name: "Standard",
    who: "Middle income, ages 35–55",
    ticket: 500,
    freq: 1.8,
    color: "#3D7B7B",
  },
  {
    name: "Basic",
    who: "Lower income, ages 25–45, newer homes",
    ticket: 300,
    freq: 1.2,
    color: "#8A8A78",
  },
];

const REPLACEMENT = [
  { range: "Under 5 yrs", weight: 0.1, why: "New construction, minimal needs" },
  { range: "5–9 yrs", weight: 0.3, why: "Starting to need maintenance" },
  { range: "10–14 yrs", weight: 0.6, why: "Regular maintenance phase" },
  { range: "15–19 yrs", weight: 1.0, why: "Peak replacement cycle" },
  { range: "20–24 yrs", weight: 0.7, why: "Post-replacement lull" },
  { range: "25–29 yrs", weight: 0.8, why: "Building back up" },
  { range: "30–34 yrs", weight: 1.1, why: "Second major cycle" },
  { range: "35+ yrs", weight: 1.2, why: "Ongoing high needs" },
];

const TRADES = [
  { name: "HVAC", multiplier: 1.0, note: "Full market value (baseline)" },
  { name: "Roofing", multiplier: 1.0, note: "Full market value" },
  { name: "Plumbing", multiplier: 0.7, note: "Smaller typical job size" },
  { name: "Electrical", multiplier: 0.5, note: "Smallest typical job size" },
];

const CLIMATE = [
  { zone: "Hot & Humid", adj: 1.3, note: "Year-round AC demand" },
  { zone: "Hot & Dry", adj: 1.2, note: "High cooling needs" },
  { zone: "Mixed", adj: 1.0, note: "Balanced" },
  { zone: "Cold", adj: 0.9, note: "Shorter cooling season" },
  { zone: "Marine", adj: 0.7, note: "Mild temperatures" },
];

const fmt = (n: number) => "$" + Math.round(n).toLocaleString("en-US");
const pct = (n: number) => Math.round(n * 100) + "%";

function Section({
  number,
  title,
  children,
  isDark,
}: {
  number: string;
  title: string;
  children: React.ReactNode;
  isDark: boolean;
}) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: "#B8432F",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {number}
        </div>
        <h3
          style={{
            margin: 0,
            fontSize: 17,
            fontWeight: 700,
            color: isDark ? "#f1f5f9" : "#1A1B23",
          }}
        >
          {title}
        </h3>
      </div>
      {children}
    </div>
  );
}

function MiniTable({
  headers,
  rows,
  highlightCol,
  isDark,
}: {
  headers: string[];
  rows: (string | number)[][];
  highlightCol?: number;
  isDark: boolean;
}) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                style={{
                  textAlign: "left",
                  padding: "8px 12px",
                  borderBottom: isDark
                    ? "2px solid #334155"
                    : "2px solid #E8E8EC",
                  color: isDark ? "#94a3b8" : "#6B6D7B",
                  fontWeight: 600,
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  whiteSpace: "nowrap",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  style={{
                    padding: "8px 12px",
                    borderBottom: isDark
                      ? "1px solid #1e293b"
                      : "1px solid #F0F0F2",
                    color:
                      ci === highlightCol
                        ? "#B8432F"
                        : isDark
                          ? "#e2e8f0"
                          : "#1A1B23",
                    fontWeight: ci === highlightCol ? 700 : 400,
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: ci === row.length - 1 ? "normal" : "nowrap",
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LiveExample({ isDark }: { isDark: boolean }) {
  const [homes, setHomes] = useState(1000);
  const [segIdx, setSegIdx] = useState(1);
  const [repIdx, setRepIdx] = useState(4);
  const [tradeIdx, setTradeIdx] = useState(0);
  const [climIdx, setClimIdx] = useState(0);

  const seg = SEGMENTS[segIdx];
  const rep = REPLACEMENT[repIdx];
  const trade = TRADES[tradeIdx];
  const clim = CLIMATE[climIdx];

  const result =
    homes * seg.ticket * seg.freq * rep.weight * trade.multiplier * clim.adj;

  const steps = [
    { label: `${homes.toLocaleString()} homes`, color: "#6B6D7B" },
    { label: `${fmt(seg.ticket)} ticket`, color: seg.color },
    { label: `${seg.freq}× freq`, color: seg.color },
    { label: `${pct(rep.weight)} replacement`, color: "#6B6D7B" },
    { label: `${pct(trade.multiplier)} trade`, color: "#6B6D7B" },
    { label: `${clim.adj}× climate`, color: "#6B6D7B" },
  ];

  const textPrimary = isDark ? "#e2e8f0" : "#1A1B23";
  const textSecondary = isDark ? "#94a3b8" : "#6B6D7B";
  const borderColor = isDark ? "#334155" : "#E8E8EC";
  const borderColorLight = isDark ? "#1e293b" : "#E0E0E4";
  const bgSurface = isDark ? "#1e293b" : "#fff";
  const bgChip = isDark ? "#334155" : "#F0F0F2";

  return (
    <div
      style={{
        background: isDark ? "#0f172a" : "#FAFAFB",
        borderRadius: 12,
        padding: 20,
        border: `1px solid ${borderColor}`,
      }}
    >
      <div
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: textSecondary,
          fontWeight: 600,
          marginBottom: 14,
        }}
      >
        Try it yourself
      </div>

      {/* Controls */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 14,
          marginBottom: 20,
        }}
      >
        <div>
          <label
            style={{
              fontSize: 12,
              color: textSecondary,
              display: "block",
              marginBottom: 6,
            }}
          >
            Owner-occupied homes
          </label>
          <input
            max={10000}
            min={100}
            onChange={(e) => setHomes(Number(e.target.value))}
            step={100}
            style={{ width: "100%", accentColor: "#B8432F" }}
            type="range"
            value={homes}
          />
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: textPrimary,
              marginTop: 2,
            }}
          >
            {homes.toLocaleString()} homes
          </div>
        </div>

        <div>
          <label
            style={{
              fontSize: 12,
              color: textSecondary,
              display: "block",
              marginBottom: 6,
            }}
          >
            Customer segment (showing one for simplicity)
          </label>
          <div style={{ display: "flex", gap: 6 }}>
            {SEGMENTS.map((s, i) => (
              <button
                key={i}
                onClick={() => setSegIdx(i)}
                style={{
                  padding: "5px 14px",
                  borderRadius: 8,
                  border:
                    i === segIdx
                      ? `2px solid ${s.color}`
                      : `1px solid ${borderColorLight}`,
                  background: i === segIdx ? s.color + "10" : bgSurface,
                  color: i === segIdx ? s.color : textSecondary,
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label
            style={{
              fontSize: 12,
              color: textSecondary,
              display: "block",
              marginBottom: 6,
            }}
          >
            Median home age:{" "}
            <strong style={{ color: textPrimary }}>
              {REPLACEMENT[repIdx].range}
            </strong>
          </label>
          <input
            max={REPLACEMENT.length - 1}
            min={0}
            onChange={(e) => setRepIdx(Number(e.target.value))}
            style={{ width: "100%", accentColor: "#B8432F" }}
            type="range"
            value={repIdx}
          />
        </div>

        <div style={{ display: "flex", gap: 16 }}>
          <div style={{ flex: 1 }}>
            <label
              style={{
                fontSize: 12,
                color: textSecondary,
                display: "block",
                marginBottom: 6,
              }}
            >
              Trade
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {TRADES.map((t, i) => (
                <button
                  key={i}
                  onClick={() => setTradeIdx(i)}
                  style={{
                    padding: "4px 10px",
                    borderRadius: 6,
                    border:
                      i === tradeIdx
                        ? "2px solid #B8432F"
                        : `1px solid ${borderColorLight}`,
                    background: i === tradeIdx ? "#B8432F10" : bgSurface,
                    color: i === tradeIdx ? "#B8432F" : textSecondary,
                    fontWeight: 600,
                    fontSize: 11,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <label
              style={{
                fontSize: 12,
                color: textSecondary,
                display: "block",
                marginBottom: 6,
              }}
            >
              Climate
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {CLIMATE.map((c, i) => (
                <button
                  key={i}
                  onClick={() => setClimIdx(i)}
                  style={{
                    padding: "4px 10px",
                    borderRadius: 6,
                    border:
                      i === climIdx
                        ? "2px solid #B8432F"
                        : `1px solid ${borderColorLight}`,
                    background: i === climIdx ? "#B8432F10" : bgSurface,
                    color: i === climIdx ? "#B8432F" : textSecondary,
                    fontWeight: 600,
                    fontSize: 11,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {c.zone}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          background: bgSurface,
          borderRadius: 10,
          padding: 16,
          border: `1px solid ${borderColor}`,
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 6,
            marginBottom: 12,
            fontSize: 13,
            lineHeight: 2,
          }}
        >
          {steps.map((s, i) => (
            <span
              key={i}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <span
                style={{
                  background: bgChip,
                  padding: "2px 8px",
                  borderRadius: 6,
                  fontWeight: 600,
                  color: textPrimary,
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                }}
              >
                {s.label}
              </span>
              {i < steps.length - 1 && (
                <span style={{ color: isDark ? "#475569" : "#CBCBD0" }}>×</span>
              )}
            </span>
          ))}
        </div>
        <div
          style={{
            borderTop: `2px solid ${borderColor}`,
            paddingTop: 12,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <span style={{ fontSize: 12, color: textSecondary, fontWeight: 600 }}>
            {seg.name} segment market value
          </span>
          <span
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: textPrimary,
              letterSpacing: "-0.02em",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {fmt(result)}
          </span>
        </div>
        <div
          style={{
            fontSize: 11,
            color: isDark ? "#64748b" : "#9B9DA8",
            marginTop: 4,
            textAlign: "right",
          }}
        >
          Full market = sum of all three segments
        </div>
      </div>
    </div>
  );
}

const TotalAddressableMarketDrawer: React.FC<TAMDrawerProps> = ({
  isOpen,
  onClose,
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  return (
    <Transition as={Fragment} show={isOpen}>
      <Dialog className="relative z-40" onClose={onClose}>
        <TransitionChild>
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <TransitionChild>
                <DialogPanel className="pointer-events-auto w-screen max-w-2xl">
                  <div className="flex h-full flex-col bg-white dark:bg-slate-800 shadow-xl">
                    <div className="flex items-center justify-between px-4 py-4 bg-primary">
                      <DialogTitle className="text-lg font-medium text-white">
                        How TAM is Calculated
                      </DialogTitle>
                      <button
                        className="text-white hover:text-gray-200"
                        onClick={onClose}
                        type="button"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>

                    <div
                      className="flex-1 overflow-y-auto"
                      style={{
                        background: isDark ? "#1e293b" : "#fff",
                        padding: "24px 16px",
                        fontFamily:
                          '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                        color: isDark ? "#e2e8f0" : "#1A1B23",
                        lineHeight: 1.6,
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          maxWidth: 640,
                          margin: "0 auto",
                        }}
                      >
                        <p
                          style={{
                            margin: "0 0 24px",
                            color: isDark ? "#94a3b8" : "#6B6D7B",
                            fontSize: 15,
                          }}
                        >
                          For each zip code, we estimate the total market
                          opportunity using housing data, customer demographics,
                          and real transaction history.
                        </p>

                        <div
                          style={{
                            background: isDark ? "#0f172a" : "#1A1B23",
                            borderRadius: 12,
                            padding: "18px 20px",
                            marginBottom: 32,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 11,
                              color: "#8B8D98",
                              marginBottom: 8,
                              fontWeight: 600,
                              letterSpacing: "0.05em",
                              textTransform: "uppercase",
                            }}
                          >
                            Base formula
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            {[
                              "Homes",
                              "Segment Mix",
                              "Spending",
                              "Replacement Need",
                              "Trade Factor",
                              "Climate",
                            ].map((term, i, arr) => (
                              <span
                                key={i}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <span
                                  style={{
                                    background: "rgba(255,255,255,0.08)",
                                    border: "1px solid rgba(255,255,255,0.12)",
                                    padding: "4px 12px",
                                    borderRadius: 8,
                                    color: "#fff",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {term}
                                </span>
                                {i < arr.length - 1 && (
                                  <span style={{ color: "#555", fontSize: 14 }}>
                                    ×
                                  </span>
                                )}
                              </span>
                            ))}
                          </div>
                        </div>

                        <Section
                          isDark={isDark}
                          number="1"
                          title="Customer Segments"
                        >
                          <p
                            style={{
                              margin: "0 0 12px",
                              color: isDark ? "#94a3b8" : "#6B6D7B",
                              fontSize: 14,
                            }}
                          >
                            Homeowners are grouped into three spending tiers
                            based on age, income, and home characteristics. The
                            mix is calculated from actual local data when
                            available, otherwise defaults to 25% / 50% / 25%.
                          </p>
                          <MiniTable
                            headers={[
                              "Segment",
                              "Who they are",
                              "Avg ticket",
                              "Frequency",
                            ]}
                            highlightCol={2}
                            isDark={isDark}
                            rows={SEGMENTS.map((s) => [
                              s.name,
                              s.who,
                              fmt(s.ticket),
                              s.freq + "×/yr",
                            ])}
                          />
                        </Section>

                        <Section
                          isDark={isDark}
                          number="2"
                          title="Replacement Weight"
                        >
                          <p
                            style={{
                              margin: "0 0 12px",
                              color: isDark ? "#94a3b8" : "#6B6D7B",
                              fontSize: 14,
                            }}
                          >
                            Older homes need more service. Systems follow a
                            ~15-year replacement cycle, so demand peaks, dips
                            after replacement, then builds again.
                          </p>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-end",
                              gap: 3,
                              height: 80,
                              marginBottom: 12,
                              padding: "0 4px",
                            }}
                          >
                            {REPLACEMENT.map((r, i) => (
                              <div
                                key={i}
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <div
                                  style={{
                                    width: "100%",
                                    height: Math.max(4, r.weight * 64),
                                    background:
                                      r.weight >= 1.0
                                        ? "#B8432F"
                                        : r.weight >= 0.7
                                          ? "#3D7B7B"
                                          : "#CBCBD0",
                                    borderRadius: 4,
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: 9,
                                    color: "#8B8D98",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {r.range
                                    .replace(" yrs", "")
                                    .replace(" years", "")}
                                </span>
                              </div>
                            ))}
                          </div>
                          <MiniTable
                            headers={["Home age", "Weight", "Why"]}
                            highlightCol={1}
                            isDark={isDark}
                            rows={REPLACEMENT.map((r) => [
                              r.range,
                              pct(r.weight),
                              r.why,
                            ])}
                          />
                        </Section>

                        <Section
                          isDark={isDark}
                          number="3"
                          title="Trade Multipliers"
                        >
                          <p
                            style={{
                              margin: "0 0 12px",
                              color: isDark ? "#94a3b8" : "#6B6D7B",
                              fontSize: 14,
                            }}
                          >
                            Different trades have different market sizes
                            relative to HVAC, which serves as the baseline.
                          </p>
                          <MiniTable
                            headers={["Trade", "Multiplier", "Why"]}
                            highlightCol={1}
                            isDark={isDark}
                            rows={TRADES.map((t) => [
                              t.name,
                              pct(t.multiplier),
                              t.note,
                            ])}
                          />
                        </Section>

                        <Section
                          isDark={isDark}
                          number="4"
                          title="Climate Adjustment"
                        >
                          <p
                            style={{
                              margin: "0 0 12px",
                              color: isDark ? "#94a3b8" : "#6B6D7B",
                              fontSize: 14,
                            }}
                          >
                            HVAC demand varies significantly by climate zone.
                            Hotter regions drive higher AC usage and more
                            frequent replacements.
                          </p>
                          <MiniTable
                            headers={["Climate zone", "Adjustment", "Reason"]}
                            highlightCol={1}
                            isDark={isDark}
                            rows={CLIMATE.map((c) => [
                              c.zone,
                              c.adj > 1
                                ? "+" + Math.round((c.adj - 1) * 100) + "%"
                                : c.adj < 1
                                  ? Math.round((c.adj - 1) * 100) + "%"
                                  : "Baseline",
                              c.note,
                            ])}
                          />
                        </Section>

                        <Section
                          isDark={isDark}
                          number="5"
                          title="Example Calculation"
                        >
                          <p
                            style={{
                              margin: "0 0 14px",
                              color: isDark ? "#94a3b8" : "#6B6D7B",
                              fontSize: 14,
                            }}
                          >
                            Adjust the inputs below to see how each factor
                            affects the market value for a single customer
                            segment.
                          </p>
                          <LiveExample isDark={isDark} />
                        </Section>

                        <div
                          style={{
                            marginTop: 16,
                            padding: "16px 0",
                            borderTop: isDark
                              ? "1px solid #334155"
                              : "1px solid #E0E0E4",
                            fontSize: 12,
                            color: isDark ? "#64748b" : "#9B9DA8",
                            textAlign: "center",
                          }}
                        >
                          Data sources: US Census (housing units, demographics)
                          · CertainPath prospect database · Validated against
                          actual invoice revenue
                        </div>
                      </div>
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TotalAddressableMarketDrawer;
